import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";

const LANGS = ["ro", "en", "de", "it", "he", "pl"];

export default function Header({ config }) {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["#camere", t("nav.rooms")],
    ["#galerie", t("nav.gallery")],
    ["#experiente", t("nav.experiences")],
    ["#atractii", t("nav.attractions")],
    ["#evenimente", t("nav.events")],
    ["#retreats", t("nav.retreats")],
    ["#recenzii", t("nav.reviews")],
    ["#faq", t("nav.faq")],
  ];

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + id);
    } else {
      scrollToId(id);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-cream/80 border-b border-warm-grey/40" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <button onClick={() => go("#top")} data-testid="header-logo" className="shrink-0">
          <img src="/assets/logo-line.webp" alt="Imperfect House" className="h-9 md:h-11 w-auto mix-blend-multiply" />
        </button>

        <nav className="hidden lg:flex items-center gap-7" data-testid="header-nav">
          {links.map(([id, label]) => (
            <button
              key={id}
              onClick={() => go(id)}
              data-testid={`nav-${id.slice(1)}`}
              className="text-sm font-medium text-brown hover:text-terracotta transition-colors duration-300"
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <div className="flex items-center gap-1 text-xs font-bold tracking-widest" data-testid="lang-switcher">
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`lang-${l}`}
                className={`px-1.5 py-1 uppercase transition-colors duration-300 ${
                  lang === l ? "text-terracotta" : "text-walnut/60 hover:text-brown"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            onClick={() => go("#rezervare")}
            data-testid="header-cta"
            className="hidden md:inline-flex items-center rounded-full bg-terracotta text-cream-light text-sm font-semibold px-5 py-2.5 hover:bg-terracotta/90 hover:scale-[1.03] transition-all duration-300"
          >
            {t("nav.book")}
          </button>
          <button
            className="lg:hidden text-brown"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label={t("nav.menu")}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-y-auto max-h-[80vh] bg-cream/95 backdrop-blur-xl border-b border-warm-grey/40"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => go(id)}
                  data-testid={`mobile-nav-${id.slice(1)}`}
                  className="text-left text-lg font-serif text-brown"
                >
                  {label}
                </button>
              ))}
              <button
                onClick={() => go("#rezervare")}
                data-testid="mobile-cta"
                className="mt-2 rounded-full bg-terracotta text-cream-light font-semibold px-5 py-3 text-center"
              >
                {t("nav.book")}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
